/* ============================================
   useAuth Hook — JWT Authentication
   Manages session state, login, logout
   Multi-cuenta: una identidad (email) puede tener
   varias membresías (empresa + rol). El token lleva
   la "cuenta activa"; switchAccount la cambia.
   ============================================ */

import { useState, useEffect, useCallback } from 'react'

/*
  `ventas` es gente nuestra con el ERP recortado: vive en el dominio del admin
  igual que un `admin`, pero sólo ve los módulos de `usuarios.modulos` y, dentro
  de ellos, su cartera. `cliente` y `partner` son de afuera y viven en el otro
  dominio.
*/
export type UserRole = 'admin' | 'ventas' | 'cliente' | 'partner'

export interface Account {
  cliente_id: number
  role: UserRole
  empresa: string | null
  es_principal?: boolean
}

interface AuthUser {
  id: number
  email: string
  role: UserRole | null
  contacto_id: number | null
  cliente_id: number | null
  must_change_password: boolean
  // Roles del contacto en la empresa activa (PRINCIPAL/FACTURACIÓN/...).
  roles?: string[]
  user_metadata?: Record<string, any>
  /** Para el pie del sidebar. Sin nombre, el avatar cae a las iniciales del email. */
  nombre?: string | null
  foto_url?: string | null
  /** Módulos visibles. `null` = sin recorte (admin). Sale del JWT, vía /auth/me. */
  modulos?: string[] | null
}

interface AuthState {
  user: AuthUser | null
  token: string | null
  loading: boolean
}

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

/** Avisa a todas las copias de `useAuth` que el usuario cambió (foto, nombre). */
const EVENTO_REFRESCO = 'auth:refrescar-usuario'

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: localStorage.getItem('auth_token'),
    loading: true,
  })
  const [accounts, setAccounts] = useState<Account[]>([])

  // Fetch the list of accounts (memberships) for the switcher
  const loadAccounts = useCallback(async (token: string) => {
    try {
      const res = await fetch(`${API_URL}/api/auth/accounts`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (res.ok) {
        const data = await res.json()
        setAccounts(Array.isArray(data.accounts) ? data.accounts : [])
      }
    } catch {
      /* silently ignore — switcher just won't show */
    }
  }, [])

  /*
    `useAuth` es un hook con estado LOCAL, no un contexto: cada pantalla que lo
    llama tiene su propia copia del usuario. Así que cuando el perfil cambia la
    foto o el nombre, el pie del sidebar —que es otra copia— se queda con los
    anteriores hasta recargar la página entera.

    El evento lo arregla sin convertir esto en un contexto: lo escuchan TODAS
    las copias y cada una vuelve a preguntar quién es. `refreshUser()` solo lo
    dispara, así que sirve desde cualquier pantalla sin pasarse nada.
  */
  const refreshUser = useCallback(() => {
    window.dispatchEvent(new Event(EVENTO_REFRESCO))
  }, [])

  useEffect(() => {
    const alRefrescar = () => {
      const token = localStorage.getItem('auth_token')
      if (!token) return
      fetch(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => (r.ok ? r.json() : null))
        .then(data => { if (data?.user) setState(prev => ({ ...prev, user: data.user })) })
        .catch(() => { /* Si falla, se queda con lo que ya tenía: no se cierra sesión por esto. */ })
    }
    window.addEventListener(EVENTO_REFRESCO, alRefrescar)
    return () => window.removeEventListener(EVENTO_REFRESCO, alRefrescar)
  }, [])

  // Verify token on mount
  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    if (token) {
      fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => {
          if (r.ok) return r.json()
          throw new Error('Invalid token')
        })
        .then((data) => {
          setState({ user: data.user, token, loading: false })
          // Ventas no tiene membresías (no pertenece a ninguna empresa
          // cliente), así que el selector de cuentas no aplica.
          const rol = data.user?.role
          if (rol && rol !== 'admin' && rol !== 'ventas') loadAccounts(token)
        })
        .catch(() => {
          localStorage.removeItem('auth_token')
          setState({ user: null, token: null, loading: false })
        })
    } else {
      setState({ user: null, token: null, loading: false })
    }
  }, [loadAccounts])

  const signIn = useCallback(async (email: string, password: string): Promise<{ multi: boolean; role: UserRole | null }> => {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })

    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Error de autenticación' }))
      throw new Error(error.error || 'Credenciales incorrectas')
    }

    const data = await res.json()
    localStorage.setItem('auth_token', data.token)
    setAccounts(Array.isArray(data.accounts) ? data.accounts : [])

    if (data.multi) {
      // Varias cuentas → aún sin cuenta activa; el componente redirige al selector
      setState({
        user: { id: data.user.id, email: data.user.email, role: null, contacto_id: null, cliente_id: null, must_change_password: !!data.user.must_change_password },
        token: data.token,
        loading: false,
      })
      return { multi: true, role: null }
    }

    setState({ user: data.user, token: data.token, loading: false })
    return { multi: false, role: (data.user?.role ?? null) as UserRole | null }
  }, [])

  // Cambiar / elegir la cuenta activa → re-emite un JWT scopeado
  const switchAccount = useCallback(async (clienteId: number): Promise<UserRole> => {
    const token = localStorage.getItem('auth_token')
    if (!token) throw new Error('No autenticado')

    const res = await fetch(`${API_URL}/api/auth/switch-account`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ cliente_id: clienteId }),
    })

    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'No se pudo cambiar de cuenta' }))
      throw new Error(error.error || 'No se pudo cambiar de cuenta')
    }

    const data = await res.json()
    localStorage.setItem('auth_token', data.token)
    setState({ user: data.user, token: data.token, loading: false })
    return data.user.role as UserRole
  }, [])

  const signOut = useCallback(() => {
    localStorage.removeItem('auth_token')
    setAccounts([])
    setState({ user: null, token: null, loading: false })
  }, [])

  const resetPassword = useCallback(async (email: string) => {
    const res = await fetch(`${API_URL}/api/auth/forgot`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })

    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Error al enviar email' }))
      throw new Error(error.error || 'Error al enviar email de recuperación')
    }
  }, [])

  const updatePassword = useCallback(async (newPassword: string) => {
    const token = localStorage.getItem('auth_token')
    if (!token) throw new Error('No autenticado')

    const res = await fetch(`${API_URL}/api/auth/password`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ password: newPassword }),
    })

    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Error al actualizar' }))
      throw new Error(error.error || 'Error al actualizar contraseña')
    }
  }, [])

  // Derived role and metadata
  const role: UserRole | null = state.user?.role ?? null
  const isAdmin = role === 'admin'
  const isVentas = role === 'ventas'
  /** Gente de adentro: admin o ventas. Es quien vive en el dominio del ERP. */
  const esEquipo = isAdmin || isVentas
  const isCliente = role === 'cliente'
  const isPartner = role === 'partner'
  const modulos = state.user?.modulos ?? null
  // Sin cuenta activa: autenticado (identidad) pero todavía sin rol → debe elegir cuenta
  const needsAccountSelection = !!state.user && role === null
  const mustChangePassword = !!state.user?.must_change_password
  const contactoId = state.user?.contacto_id ?? undefined
  const clienteId = state.user?.cliente_id ?? undefined

  return {
    user: state.user,
    token: state.token,
    session: state.token ? { access_token: state.token } : null,  // backward compat
    loading: state.loading,
    isAuthenticated: !!state.user,
    role,
    isAdmin,
    isVentas,
    esEquipo,
    modulos,
    isCliente,
    isPartner,
    needsAccountSelection,
    accounts,
    hasMultipleAccounts: accounts.length > 1,
    activeClienteId: clienteId,
    mustChangePassword,
    contactoId,
    clienteId,
    signIn,
    refreshUser,
    switchAccount,
    signOut,
    resetPassword,
    updatePassword,
  }
}
